// Answer digests (contracts/README.md "Answers").
//
//   canonical: choice  -> the option id, e.g. "C"
//              cellSet -> cells sorted by row then column, "r,c;r,c;…"
//              number, digits, fill, sequence, rows (launch types) -> see launch/answers.ts
//   digest    = lowercase hex SHA-256 of "<spec.id>|<canonical>"
//
// Uses Web Crypto (browser and Node >= 20). Level packs carry `check.digest`;
// admin previews use this to prove a recovered answer matches the pack.

import { useEffect, useState } from "react";
import { canonicalLaunchAnswer, digitsLength, rowsCount } from "./launch/answers";
import type { Answer, Board, CellRef, DigitsAnswerSpec, NumberAnswerSpec, PuzzleSpec, RowsAnswerSpec, SequenceAnswerSpec } from "./types";
import { kindOf } from "./types";

export function canonicalCells(cells: readonly CellRef[]): string {
  return [...cells]
    .map(([r, c]) => [Math.trunc(Number(r)), Math.trunc(Number(c))] as const)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .map(([r, c]) => `${r},${c}`)
    .join(";");
}

export function canonicalAnswer(answer: Answer): string {
  switch (answer.kind) {
    case "choice":
      return String(answer.value);
    case "cellSet":
      return canonicalCells(answer.value);
    case "number":
    case "digits":
    case "fill":
    case "sequence":
    case "rows":
      return canonicalLaunchAnswer(answer);
    default: {
      const kind = (answer as { kind?: unknown }).kind;
      throw new Error(`No canonical form for answer kind ${String(kind)}`);
    }
  }
}

export async function sha256Hex(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("Web Crypto (crypto.subtle) is not available in this context");
  const bytes = new TextEncoder().encode(text);
  const hash = await subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** sha256_hex("<specId>|<canonical answer>") */
export function answerDigest(specId: string, answer: Answer): Promise<string> {
  return sha256Hex(`${specId}|${canonicalAnswer(answer)}`);
}

export type DigestStatus = "none" | "pending" | "verified" | "mismatch" | "error";

/** Compares an answer with the spec's `check.digest`. "none" when the spec carries no check. */
export async function verifyAnswer(spec: Pick<PuzzleSpec, "id" | "check">, answer: Answer): Promise<DigestStatus> {
  const expected = spec.check?.digest;
  if (!expected) return "none";
  try {
    const actual = await answerDigest(spec.id, answer);
    return actual === expected.toLowerCase() ? "verified" : "mismatch";
  } catch {
    return "error";
  }
}

/**
 * Recovers an answer from a level spec's digest, the way the server does for review:
 * choice -> try each option id; cellSet with a watch phase -> the lit cells of the watch board; launch
 * kinds -> see recoverLaunchAnswer.
 * Returns null when nothing matches.
 */
export async function recoverAnswer(spec: PuzzleSpec): Promise<Answer | null> {
  const expected = spec.check?.digest?.toLowerCase();
  if (!expected) return null;
  if (spec.answer?.kind === "choice") {
    for (const option of spec.answer.options ?? []) {
      const candidate: Answer = { kind: "choice", value: option.id };
      if ((await answerDigest(spec.id, candidate)) === expected) return candidate;
    }
    return null;
  }
  if (spec.answer?.kind === "cellSet") {
    const watch = spec.phases?.find((p) => p.kind === "watch");
    const board = watch && "board" in watch ? watch.board : undefined;
    if (board?.kind !== "grid") return null;
    const cells: CellRef[] = [];
    board.cells.forEach((cell, i) => {
      if (cell?.lit) cells.push([Math.floor(i / board.cols), i % board.cols]);
    });
    const candidate: Answer = { kind: "cellSet", value: cells };
    return (await answerDigest(spec.id, candidate)) === expected ? candidate : null;
  }
  return recoverLaunchAnswer(spec, expected);
}

const watchBoard = (spec: PuzzleSpec): Board | undefined => {
  const watch = spec.phases?.find((p) => p.kind === "watch");
  return watch && "board" in watch ? watch.board : undefined;
};

const textOf = (g: unknown) => (kindOf(g) === "text" ? String((g as { value?: unknown }).value ?? "").trim() : "");

/**
 * Launch kinds, where a small search can find the answer (admin reveal for level packs):
 * digits -> the digits of the watch board (Digit Span); sequence -> the watch grid's numbered cells in
 * number order (Order Tap); rows -> every Y/N string (2^count); number -> every integer the keypad
 * allows when that's at most 20 000 candidates. Anything bigger (fill) is left to the server.
 */
async function recoverLaunchAnswer(spec: PuzzleSpec, expected: string): Promise<Answer | null> {
  const matches = async (a: Answer) => {
    try {
      return (await answerDigest(spec.id, a)) === expected;
    } catch {
      return false;
    }
  };
  const a = spec.answer;
  const board = watchBoard(spec);
  switch (kindOf(a)) {
    case "digits": {
      const items = kindOf(board) === "sequence" ? ((board as { items?: unknown[] }).items ?? []) : [];
      const digits = items.map(textOf).join("");
      const candidate: Answer = { kind: "digits", value: digits };
      return /^\d+$/.test(digits) && digits.length === digitsLength(a as DigitsAnswerSpec) && (await matches(candidate)) ? candidate : null;
    }
    case "sequence": {
      if (kindOf(board) !== "grid") return null;
      const grid = board as { cols?: number; cells?: { glyph?: unknown }[] };
      const cols = Math.max(1, Math.trunc(Number(grid.cols) || 1));
      const numbered = (grid.cells ?? [])
        .map((c, i) => ({ n: Number(textOf(c?.glyph)), cell: [Math.floor(i / cols), i % cols] as CellRef }))
        .filter((x) => Number.isInteger(x.n) && x.n > 0)
        .sort((x, y) => x.n - y.n)
        .map((x) => x.cell);
      const count = (a as SequenceAnswerSpec).count;
      const candidate: Answer = { kind: "sequence", value: numbered.slice(0, count) };
      return numbered.length >= 2 && (await matches(candidate)) ? candidate : null;
    }
    case "rows": {
      const count = rowsCount(a as RowsAnswerSpec);
      if (count > 12) return null;
      for (let bits = 0; bits < 1 << count; bits++) {
        const value = Array.from({ length: count }, (_, i) => ((bits >> (count - 1 - i)) & 1 ? "Y" : "N")).join("");
        const candidate: Answer = { kind: "rows", value };
        if (await matches(candidate)) return candidate;
      }
      return null;
    }
    case "number": {
      const n = a as NumberAnswerSpec;
      const digits = typeof n.maxDigits === "number" ? Math.min(6, Math.max(1, n.maxDigits)) : 3;
      let lo = typeof n.min === "number" ? n.min : n.allowNegative ? -(10 ** digits - 1) : 0;
      let hi = typeof n.max === "number" ? n.max : 10 ** digits - 1;
      if (lo > hi) [lo, hi] = [hi, lo];
      if (hi - lo > 20_000) return null;
      for (let v = lo; v <= hi; v++) {
        const candidate: Answer = { kind: "number", value: String(v) };
        if (await matches(candidate)) return candidate;
      }
      return null;
    }
    default:
      return null;
  }
}

/** React hook: verifies `answer` against `spec.check`. */
export function useDigestVerification(
  spec: Pick<PuzzleSpec, "id" | "check"> | null | undefined,
  answer: Answer | null | undefined,
): DigestStatus {
  const key = spec && answer ? `${spec.id}|${spec.check?.digest ?? ""}|${safeCanonical(answer)}` : "";
  const [result, setResult] = useState<{ key: string; status: DigestStatus }>({ key: "", status: "none" });

  useEffect(() => {
    if (!spec || !answer) return;
    let cancelled = false;
    verifyAnswer(spec, answer).then((status) => {
      if (!cancelled) setResult({ key, status });
    });
    return () => {
      cancelled = true;
    };
    // `key` captures everything that matters about spec and answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!spec || !answer) return "none";
  if (!spec.check?.digest) return "none";
  return result.key === key ? result.status : "pending";
}

function safeCanonical(answer: Answer): string {
  try {
    return canonicalAnswer(answer);
  } catch {
    return "?";
  }
}
